import { Ionicons } from '@expo/vector-icons';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import * as Haptics from 'expo-haptics';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useActiveAlarmsCount } from '../hooks/useActiveAlarms';
import { useWebSocket } from '../hooks/useRealtime';
import { useWebSocketQueryBridge } from '../hooks/useWebSocketQueryBridge';

// Import screens
import { Platform, StyleSheet, View } from 'react-native';
import GalaxyHeaderBackground from '../components/GalaxyHeaderBackground';
import DashboardScreen from '../screens/DashboardScreen';
import KiosksManagementScreen from '../screens/KiosksManagementScreen';
import MeetingRoomScheduleScreen from '../screens/MeetingRoomScheduleScreen';
import ProfileScreen from '../screens/ProfileScreen';
import { canManageSitesAndZones, canManageUsers } from '../utils/rbac';
import AlarmsStackNavigator from './AlarmsStackNavigator';
import DevicesStackNavigator from './DevicesStackNavigator';

const styles = StyleSheet.create({
  tabIcon: {
    width: 34,
    height: 28,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeDot: {
    position: 'absolute',
    bottom: 2,
    width: 4,
    height: 4,
    borderRadius: 2,
  },
});

export type MainTabParamList = {
  Dashboard: undefined;
  Devices: {
    filter?: 'all' | 'online' | 'offline';
    refresh?: boolean;
  };
  Alarms: {
    severity?: 'all' | 'critical' | 'warning';
    refresh?: boolean;
  };
  MeetingRoomSchedule: undefined;
  KiosksManagement: undefined;
  Profile: undefined;
};

const Tab = createBottomTabNavigator<MainTabParamList>();

export default function MainTabNavigator() {
  const { t, i18n } = useTranslation();
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const isArabic = i18n.language === 'ar';
  const canManageKiosks = canManageSitesAndZones(user) || canManageUsers(user);

  // ✅ React Query: alarm badge with auto-refresh + WebSocket invalidation
  const { data: activeAlarmsCount = 0 } = useActiveAlarmsCount(user?.orgId);

  // ✅ إدارة اتصال WebSocket على مستوى التطبيق (دائماً نشط)
  useWebSocket();
  // ✅ Bridge WebSocket events → React Query cache invalidation
  useWebSocketQueryBridge();

  // Calculate tab bar height with safe area
  const TAB_BAR_BASE_HEIGHT = 64;
  const tabBarHeight = TAB_BAR_BASE_HEIGHT + insets.bottom;
  const tabBarPaddingBottom = Math.max(insets.bottom, 4);

  return (
    <Tab.Navigator
      // ✅ P1-08: Navigation performance optimizations
      detachInactiveScreens={true} // Allow RN to manage memory - keeps state but detaches UI when inactive
      screenListeners={{
        tabPress: () => {
          if (Platform.OS !== 'web') {
            void Haptics.selectionAsync();
          }
        },
      }}
      screenOptions={({ route }) => ({
        // ✅ P1-08: Enable lazy loading for better initial performance
        lazy: true,
        tabBarIcon: ({ focused, color }) => {
          let iconName: keyof typeof Ionicons.glyphMap = 'home';

          if (route.name === 'Dashboard') {
            iconName = focused ? 'grid' : 'grid-outline';
          } else if (route.name === 'Devices') {
            iconName = focused ? 'hardware-chip' : 'hardware-chip-outline';
          } else if (route.name === 'Alarms') {
            iconName = focused ? 'notifications' : 'notifications-outline';
          } else if (route.name === 'MeetingRoomSchedule') {
            iconName = focused ? 'calendar' : 'calendar-outline';
          } else if (route.name === 'KiosksManagement') {
            iconName = focused ? 'tablet-landscape' : 'tablet-landscape-outline';
          } else if (route.name === 'Profile') {
            iconName = focused ? 'person' : 'person-outline';
          }

          return (
            <View
              style={[
                styles.tabIcon,
                focused && {
                  backgroundColor: isDark ? 'rgba(105, 121, 230, 0.28)' : 'rgba(57, 73, 171, 0.14)',
                },
              ]}
            >
              <Ionicons name={iconName} size={focused ? 22 : 21} color={color} />
              {focused && <View style={[styles.activeDot, { backgroundColor: color }]} />}
            </View>
          );
        },
        tabBarActiveTintColor: theme.colors.primary,
        tabBarInactiveTintColor: theme.colors.textSecondary,
        tabBarHideOnKeyboard: true,
        tabBarStyle: {
          marginHorizontal: 8,
          marginBottom: 6,
          backgroundColor: isDark ? 'rgba(22, 30, 52, 0.86)' : 'rgba(248, 250, 252, 0.88)',
          borderColor: isDark ? 'rgba(203, 213, 225, 0.24)' : 'rgba(100, 116, 139, 0.16)',
          borderTopWidth: 1,
          borderWidth: 1,
          borderRadius: 999,
          overflow: 'hidden',
          height: tabBarHeight,
          paddingBottom: tabBarPaddingBottom,
          paddingTop: 4,
          paddingHorizontal: 1,
          elevation: 12,
          shadowColor: theme.colors.shadow,
          shadowOffset: { width: 0, height: 6 },
          shadowOpacity: isDark ? 0.24 : 0.11,
          shadowRadius: 16,
        },
        tabBarLabelStyle: {
          fontSize: 8.5,
          fontWeight: '700',
          marginBottom: 1,
        },
        tabBarIconStyle: {
          marginTop: 0,
        },
        tabBarItemStyle: {
          borderRadius: 18,
        },
        headerStyle: {
          backgroundColor: '#3949ab',
        },
        headerTintColor: '#ffffff',
        headerTitleStyle: {
          fontWeight: 'bold',
        },
      })}
    >
      <Tab.Screen
        name="Dashboard"
        component={DashboardScreen}
        options={{
          title: t('navigation.dashboard', 'Dashboard'),
          headerShown: false,
          tabBarLabel: isArabic ? 'الرئيسية' : 'Home',
          tabBarAccessibilityLabel: t('navigation.dashboard', 'Dashboard'),
        }}
      />
      <Tab.Screen
        name="Devices"
        component={DevicesStackNavigator}
        options={{
          title: t('navigation.devices', 'Devices'),
          headerShown: false,
          tabBarLabel: t('navigation.devices', 'Devices'),
          tabBarAccessibilityLabel: t('navigation.devices', 'Devices'),
        }}
      />
      <Tab.Screen
        name="Alarms"
        component={AlarmsStackNavigator}
        options={{
          title: t('navigation.alarms', 'Alarms'),
          headerShown: false,
          tabBarLabel: t('navigation.alarms', 'Alarms'),
          tabBarAccessibilityLabel:
            activeAlarmsCount > 0
              ? `${t('navigation.alarms', 'Alarms')}, ${activeAlarmsCount} ${t(
                  'alarms.active',
                  'active',
                )}`
              : t('navigation.alarms', 'Alarms'),
          tabBarBadge: activeAlarmsCount > 0 ? activeAlarmsCount : undefined,
          tabBarBadgeStyle: {
            backgroundColor: theme.colors.error,
            color: '#ffffff',
            fontSize: 11,
            fontWeight: 'bold',
            minWidth: 18,
            height: 18,
            borderRadius: 9,
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      />
      <Tab.Screen
        name="MeetingRoomSchedule"
        component={MeetingRoomScheduleScreen}
        options={{
          title: isArabic ? 'المواعيد' : 'Schedule',
          headerShown: true,
          headerBackground: () => <GalaxyHeaderBackground />,
          headerShadowVisible: false,
          headerStyle: { backgroundColor: '#0b1654' },
          headerTintColor: '#ffffff',
          headerTitleStyle: { color: '#ffffff', fontWeight: '700' },
          tabBarLabel: isArabic ? 'المواعيد' : 'Schedule',
          tabBarAccessibilityLabel: isArabic ? 'جدول غرف الاجتماعات' : 'Meeting room schedule',
        }}
      />
      {canManageKiosks && (
        <Tab.Screen
          name="KiosksManagement"
          component={KiosksManagementScreen}
          options={{
            title: isArabic ? 'الشاشات' : 'Screens',
            headerShown: true,
            headerBackground: () => <GalaxyHeaderBackground />,
            headerShadowVisible: false,
            headerStyle: { backgroundColor: '#0b1654' },
            headerTintColor: '#ffffff',
            headerTitleStyle: { color: '#ffffff', fontWeight: '700' },
            tabBarLabel: isArabic ? 'الشاشات' : 'Screens',
            tabBarAccessibilityLabel: isArabic ? 'إدارة الشاشات' : 'Screen management',
          }}
        />
      )}
      <Tab.Screen
        name="Profile"
        component={ProfileScreen}
        options={{
          title: t('navigation.profile', 'Profile'),
          headerShown: false,
          tabBarLabel: isArabic ? 'حسابي' : 'Profile',
          tabBarAccessibilityLabel: t('navigation.profile', 'Profile'),
        }}
      />
    </Tab.Navigator>
  );
}
